import React, { useState, useEffect, useCallback } from "react";
import { Switch, Route, useLocation, useHistory } from "react-router-dom";
import "./App.css";
import {
    AppBar,
    CssBaseline,
    Toolbar,
    Typography,
    BottomNavigation,
    BottomNavigationAction,
    Icon,
    IconButton,
    Menu,
    MenuItem,
} from "@material-ui/core";
import {
    createMuiTheme,
    makeStyles,
    ThemeProvider,
} from "@material-ui/core/styles";
import { AuthContext } from "./context/auth";
import { ToolbarOptionsContext } from "./context/toolbarOptions";
import HomeScreen from "./screens/HomeScreen";
import MovieScreen from "./screens/MovieScreen";
import SharedListsScreen from "./screens/SharedListsScreen";
import SharedListScreen from "./screens/SharedListScreen";
import LoginScreen from "./screens/LoginScreen";
import CreateSharedListScreen from "./screens/CreateSharedListScreen";

const theme = createMuiTheme({
    palette: {
        type: "dark",
        primary: {
            main: "#121212",
        },
        secondary: {
            main: "#58bcf1",
        },
    },
    overrides: {
        MuiAppBar: {
            root: {
                height: "64px",
            },
        },
        MuiBottomNavigation: {
            root: {
                height: "56px",
            },
        },
    },
});

const ROUTES_TO_SHOW_BOTTOM_NAV = ["/", "/sharedlists"];

const firebase = window.firebase;

const App = () => {
    const location = useLocation();
    const history = useHistory();

    const [authState, setAuthState] = useState(null);

    const [toolbarOptions, setToolbarOptions] = useState({
        title: "Movie Pal",
        items: [],
    });

    useEffect(() => {
        firebase.auth().onAuthStateChanged((user) => {
            if (user) {
                setAuthState({ isAuthenticated: true, user });
            } else {
                setAuthState({ isAuthenticated: false });
            }
        });
    }, []);

    return (
        <AuthContext.Provider value={authState}>
            <ToolbarOptionsContext.Provider
                value={{
                    setToolbarOptions: setToolbarOptions,
                }}
            >
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <div className="App">
                        <AppBar position="sticky">
                            <Toolbar className="toolbar">
                                <Typography variant="h6" className="title">
                                    {toolbarOptions.title}
                                </Typography>
                                <ToolbarOptions
                                    pathname={location.pathname}
                                    authState={authState}
                                    options={toolbarOptions}
                                />
                            </Toolbar>
                        </AppBar>
                        <div style={getMainContentStyle(location.pathname)}>
                            <Switch>
                                <Route path="/movies/:slug">
                                    <MovieScreen />
                                </Route>
                                <Route path="/sharedlists/create">
                                    <CreateSharedListScreen />
                                </Route>
                                <Route path="/sharedlists/:id">
                                    <SharedListScreen />
                                </Route>
                                <Route path="/sharedlists">
                                    <SharedListsScreen />
                                </Route>
                                <Route path="/login">
                                    <LoginScreen />
                                </Route>
                                <Route exact path="/">
                                    <HomeScreen />
                                </Route>
                            </Switch>
                        </div>
                        <MyBottomNav
                            pathname={location.pathname}
                            history={history}
                        />
                    </div>
                </ThemeProvider>
            </ToolbarOptionsContext.Provider>
        </AuthContext.Provider>
    );
};

const ToolbarOptions = (props) => {
    const { pathname, authState, options } = props;

    const [anchor, setAnchor] = useState(null);
    const open = anchor != null;
    const handleClose = () => setAnchor(null);

    return (
        <>
            {options.items}
            {pathname === "/" && (
                <React.Fragment>
                    <IconButton
                        edge="end"
                        color="inherit"
                        aria-label="more options menu"
                        aria-controls="menu-appbar"
                        aria-haspopup="true"
                        onClick={(event) => setAnchor(event.currentTarget)}
                    >
                        <Icon>more_vert</Icon>
                    </IconButton>
                    <Menu
                        id="menu-appbar"
                        anchorEl={anchor}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        keepMounted
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                        open={open}
                        onClose={handleClose}
                    >
                        <MenuItem
                            key="logout"
                            disabled={!(authState && authState.user)}
                            onClick={() => {
                                firebase.auth().signOut();
                                handleClose();
                            }}
                        >
                            Logout
                        </MenuItem>
                        <MenuItem key="privacy_policy">
                            <a
                                href="https://movie-pal.web.app/privacy_policy.html"
                                target="_blank"
                            >
                                Privacy policy
                            </a>
                        </MenuItem>
                    </Menu>
                </React.Fragment>
            )}
        </>
    );
};

const useStyles = makeStyles({
    root: {
        "&$selected": {
            color: theme.palette.secondary.main,
        },
    },
    selected: {
        color: theme.palette.secondary.main,
    },
});

const MyBottomNav = (props) => {
    const { pathname, history } = props;

    const classes = useStyles();

    return (
        <BottomNavigation
            value={pathname === "/" ? 0 : 1}
            style={getBottomNavStyle(pathname)}
            showLabels
            onChange={(_event, newValue) => {
                switch (newValue) {
                    case 0:
                        history.replace("/");
                        break;
                    case 1:
                        if (pathname !== "/sharedlists") {
                            history.push("/sharedlists");
                        }
                        break;
                    default:
                        throw `Unhandled value ${newValue} from bottom nav.`;
                }
            }}
        >
            <BottomNavigationAction
                classes={classes}
                label="Home"
                icon={<Icon>home</Icon>}
            />
            <BottomNavigationAction
                label="Shared Lists"
                classes={classes}
                icon={<Icon>wc</Icon>}
            />
        </BottomNavigation>
    );
};

const getMainContentStyle = (pathname) => {
    const style = {
        width: "100%",
        height: `calc(100% - ${
            theme.overrides.MuiAppBar.root.height
        } - ${getBottomNavHeight(pathname)})`,
    };

    if (ROUTES_TO_SHOW_BOTTOM_NAV.includes(pathname)) {
        style["overflowY"] = "auto";
        style["position"] = "fixed";
    }

    return style;
};

const getBottomNavStyle = (pathname) => {
    const style = {
        position: "fixed",
        bottom: 0,
        width: "100%",
    };

    if (!ROUTES_TO_SHOW_BOTTOM_NAV.includes(pathname)) {
        style["display"] = "none";
    }

    return style;
};

const getBottomNavHeight = (pathname) => {
    if (ROUTES_TO_SHOW_BOTTOM_NAV.includes(pathname)) {
        return theme.overrides.MuiBottomNavigation.root.height;
    } else {
        return "0px";
    }
};

export default App;
